// Phone number entry helpers.
//
// George Bottas, Monday 12974935307 (CRM-2026-00025): the Owner Information
// phone field only accepted a number that was already in E.164 form. Anything
// pasted or typed in the way Australians actually write a mobile
// ("0412 345 678", "04 1234 5678", "(04) 1234 5678", "61412345678") either got
// mangled or silently truncated, so agents were hand-editing the field - delete
// everything, type "+61", paste the rest - about 60 seconds per number.
//
// These helpers let the field accept any sane form and store the one canonical
// E.164 value the SMS/portal side needs.
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from "libphonenumber-js";

export const DEFAULT_COUNTRY = "AU";

const safeParse = (text, country) => {
  try {
    return parsePhoneNumberFromString(text, country) || null;
  } catch {
    return null;
  }
};

const safeCallingCode = (country) => {
  try {
    return getCountryCallingCode(country);
  } catch {
    return null;
  }
};

/**
 * Reduce free-typed text to something libphonenumber can parse: digits, with a
 * leading "+" only if the user actually wrote one. Separators, brackets, the
 * "(0)" trunk hint and stray letters are dropped, and an IDD prefix typed
 * instead of "+" ("0011" in Australia, "00" almost everywhere else) becomes "+".
 */
export const toParseCandidate = (text) => {
  if (text === undefined || text === null) return "";
  const trimmed = String(text).trim();
  if (!trimmed) return "";
  const hadPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  if (hadPlus) {
    // No country calling code starts with a zero, so "+0412 345 678" is a local
    // number that picked up the field's own "+" prefix, not an overseas one.
    // This is what an agent gets by selecting the whole field and typing.
    return digits.startsWith("0") ? digits : `+${digits}`;
  }
  // No "+", so an IDD prefix is the only way this can be an overseas number.
  if (digits.startsWith("0011")) return `+${digits.slice(4)}`;
  if (digits.startsWith("00")) return `+${digits.slice(2)}`;
  return digits;
};

/**
 * Turn anything a person might paste into a canonical E.164 number, or
 * `undefined` when the text isn't a valid number at all.
 */
export const normalizePhoneInput = (text, country = DEFAULT_COUNTRY) => {
  const candidate = toParseCandidate(text);
  if (!candidate) return undefined;
  const parsed = safeParse(candidate, country || DEFAULT_COUNTRY);
  return parsed && parsed.isValid() ? parsed.number : undefined;
};

/**
 * Canonicalise a value that is already E.164-ish, leaving it untouched when it
 * isn't yet a complete number. Safe to run on every keystroke: a half-typed
 * number is never a valid one, so it passes straight through, and the moment
 * the number does become valid the leftover trunk zero
 * ("+610412345678" -> "+61412345678") is dropped.
 */
export const repairPhoneValue = (value, country = DEFAULT_COUNTRY) => {
  if (!value) return value;
  return normalizePhoneInput(value, country) || value;
};

/**
 * Prepare a stored value for display. Older records hold local AU numbers
 * ("0412345678"); react-phone-number-input only understands E.164, and would
 * otherwise flag them as invalid the moment the form opens.
 */
export const toE164Value = (value, country = DEFAULT_COUNTRY) => {
  if (value === undefined || value === null) return value;
  const str = String(value).trim();
  if (!str) return "";
  const valid = normalizePhoneInput(str, country);
  if (valid) return valid;

  // Not a complete number (bad legacy data, a partial entry). Keep the digits
  // on screen rather than blanking the field, but still hand over an
  // E.164-shaped string so the input doesn't complain about the format.
  const candidate = toParseCandidate(str);
  if (!candidate) return str;
  if (candidate.startsWith("+")) return candidate;
  const callingCode = safeCallingCode(country || DEFAULT_COUNTRY);
  const national = candidate.replace(/^0+/, "");
  if (!callingCode || !national) return str;
  return `+${callingCode}${national}`;
};

/**
 * True when the value is a complete, dialable number.
 */
export const isCompletePhoneNumber = (value, country = DEFAULT_COUNTRY) =>
  Boolean(normalizePhoneInput(value, country));
